import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  LuFolderKanban,
  LuFlaskConical,
  LuPercent,
  LuSearch,
  LuPlus,
  LuArrowRight,
  LuLayoutGrid,
  LuHistory,
  LuUsers,
  LuPlay,
  LuCircleX,
  LuTimer,
  LuListChecks,
  LuCircleAlert,
  LuTriangleAlert,
  LuInfo,
  LuCircleCheck,
  LuCalendarClock,
  LuRefreshCw,
} from 'react-icons/lu';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ProjectTypeBadge } from '../common';
import { MetricCard } from '../ui';
import TestStatusChart from './TestStatusChart';
import QualityTrends from './project/QualityTrends';
import SectionCard, { SectionEmpty } from './project/SectionCard';
import useDashboardSection from './project/useDashboardSection';
import {
  RANGE_LABEL,
  RUN_OUTCOME,
  comparisonText,
  fmtCount,
  fmtDateTime,
  fmtDuration,
  fmtPct,
  fmtRelative,
  isNum,
  runPath,
} from './project/format';
import { useProject } from '../../context/ProjectContext';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { useTheme } from '../../theme';
import '../../styles/dashboard.css';
import '../../styles/rt2-project-dashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const RANGES = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

const HEALTH_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'healthy', label: 'Healthy' },
  { id: 'no_data', label: 'No data' },
];

const ALL = 'all';

const HEALTH_ORDER = { critical: 0, attention: 1, healthy: 2, no_data: 3 };

const SEVERITY = {
  critical: { icon: LuCircleAlert, tone: 'danger' },
  warning: { icon: LuTriangleAlert, tone: 'warning' },
  info: { icon: LuInfo, tone: 'info' },
  positive: { icon: LuCircleCheck, tone: 'success' },
};

const SCHEDULE_LABEL = { ONCE: 'Once', DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly', CUSTOM: 'Custom' };

function readCssVar(name, fallback) {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

const truncate = (label, max = 18) => {
  const text = String(label ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

function HorizontalBarChart({
  labels = [],
  values = [],
  color = 'primary',
  formatValue = (v) => Number(v).toLocaleString(),
  onBarClick,
  height = 240,
}) {
  const { resolvedTheme } = useTheme();
  // data-theme is applied in ThemeProvider's effect, after this render; re-read CSS vars on the next one.
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    setThemeTick((n) => n + 1);
  }, [resolvedTheme]);

  const colors = useMemo(
    () => ({
      bar: readCssVar(`--${color}`, '#2563EB'),
      grid: readCssVar('--chart-grid', '#E2E8F0'),
      tick: readCssVar('--chart-tick', '#94A3B8'),
      tooltipBg: readCssVar('--chart-tooltip-bg', '#0F172A'),
      tooltipText: readCssVar('--chart-tooltip-text', '#F8FAFC'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeTick, color]
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    onClick: (_evt, elements) => {
      if (onBarClick && elements?.length) onBarClick(elements[0].index);
    },
    onHover: (evt, elements) => {
      if (onBarClick && evt?.native?.target) {
        evt.native.target.style.cursor = elements?.length ? 'pointer' : 'default';
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.tooltipBg,
        titleColor: colors.tooltipText,
        bodyColor: colors.tooltipText,
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (items) => labels[items[0]?.dataIndex] ?? '',
          label: (ctx) => ` ${formatValue(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { precision: 0, color: colors.tick, font: { size: 11 } },
        grid: { color: colors.grid },
        border: { display: false },
      },
      y: {
        ticks: {
          color: colors.tick,
          font: { size: 11 },
          callback: (_value, index) => truncate(labels[index]),
        },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: `${colors.bar}cc`,
        hoverBackgroundColor: colors.bar,
        borderRadius: 4,
        barThickness: 14,
      },
    ],
  };

  return (
    <div style={{ position: 'relative', height }}>
      <Bar options={options} data={data} />
    </div>
  );
}

function greetingForNow() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function timeOf(value) {
  const t = value ? new Date(value).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function DeltaText({ kpi, unit }) {
  const text = comparisonText(kpi, { unit });
  if (!text) return null;
  const cls = kpi.sentiment === 'positive' ? 'is-success' : kpi.sentiment === 'negative' ? 'is-danger' : 'pqd-muted';
  return <span className={`rt-wh-delta ${cls}`}>{text}</span>;
}

function AutomationBar({ automated, total, coverage }) {
  if (!total) return <span className="pqd-muted">—</span>;
  return (
    <div className="rt-wh-auto" title={`${automated} of ${total} automated`}>
      <div className="pqd-progress">
        <div className="pqd-progress__seg pqd-progress__seg--passed" style={{ width: `${coverage || 0}%` }} />
      </div>
      <span className="pqd-num pqd-small">{fmtPct(coverage)}</span>
    </div>
  );
}

/**
 * Workspace Home — shown when a workspace is selected but no project is in context.
 * Quality data comes from /dashboard_data/workspace/overview; the all-time result
 * breakdown and plan durations still come from the legacy dashboard payload.
 */
const WorkspaceHome = ({
  workspaceId,
  workspaceName,
  activeProjects,
  loadingProjects,
  navigateTo,
  onOpenProject,
  userName,
}) => {
  const navigate = useNavigate();
  const { selectProject } = useProject();
  const greeting = greetingForNow();
  const displayName = userName || 'there';

  const [range, setRange] = useState('30d');
  const [scopeWorkspace, setScopeWorkspace] = useState(workspaceId ? String(workspaceId) : ALL);
  const [scopeProject, setScopeProject] = useState('');
  const [query, setQuery] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');

  useEffect(() => {
    setScopeWorkspace(workspaceId ? String(workspaceId) : ALL);
    setScopeProject('');
  }, [workspaceId]);

  const allWorkspaces = scopeWorkspace === ALL;
  const overview = useDashboardSection(
    'overview',
    {
      workspace_id: allWorkspaces ? undefined : scopeWorkspace,
      project_id: scopeProject || undefined,
      range,
    },
    { enabled: Boolean(workspaceId) || allWorkspaces, base: '/dashboard_data/workspace' }
  );
  const data = overview.data;
  const kpis = data?.kpis;
  const sectionLoading = overview.loading || (!data && !overview.error);
  const rangeLabel = RANGE_LABEL[range];

  const workspaceOptions = useMemo(() => {
    const list = data?.options?.workspaces || [];
    if (workspaceId && !list.some((w) => String(w.id) === String(workspaceId))) {
      return [{ id: workspaceId, name: workspaceName || `Workspace ${workspaceId}` }, ...list];
    }
    return list;
  }, [data?.options?.workspaces, workspaceId, workspaceName]);

  const projectOptionGroups = useMemo(() => {
    const groups = new Map();
    (data?.options?.projects || []).forEach((p) => {
      const key = p.workspace_name || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return [...groups.entries()];
  }, [data?.options?.projects]);

  const scopeLabel = allWorkspaces
    ? 'All workspaces'
    : workspaceOptions.find((w) => String(w.id) === scopeWorkspace)?.name || workspaceName || 'Workspace';
  const selectedProjectName = scopeProject
    ? (data?.options?.projects || []).find((p) => String(p.project_id) === scopeProject)?.project_name
    : null;

  const changeWorkspaceScope = (value) => {
    setScopeWorkspace(value);
    setScopeProject('');
    setHealthFilter('all');
  };

  const resetScope = () => changeWorkspaceScope(workspaceId ? String(workspaceId) : ALL);
  const scopeChanged = scopeProject || scopeWorkspace !== (workspaceId ? String(workspaceId) : ALL);

  const openInProject = useCallback(
    (project, subPath) => {
      const targetWorkspace = project?.workspace_id || workspaceId;
      if (!targetWorkspace || !project?.project_id) return;
      selectProject(String(project.project_id), project.project_name);
      navigate(buildWorkspaceDashboardPath(targetWorkspace, project.project_id, subPath));
    },
    [navigate, selectProject, workspaceId]
  );

  const openProject = useCallback(
    (row) => {
      const id = row.project_id ?? row.id;
      if (row.workspace_id && String(row.workspace_id) !== String(workspaceId)) {
        openInProject({ ...row, project_id: id });
        return;
      }
      onOpenProject?.({ id, name: row.project_name ?? row.name });
    },
    [onOpenProject, openInProject, workspaceId]
  );

  const descriptions = useMemo(() => {
    const map = new Map();
    (activeProjects || []).forEach((p) => map.set(String(p.id), p.description));
    return map;
  }, [activeProjects]);

  const projects = useMemo(() => {
    if (data?.projects) return data.projects;
    if (allWorkspaces || scopeProject) return [];
    return (activeProjects || []).map((p) => ({
      project_id: p.id,
      project_name: p.name,
      project_type: p.type,
      updated_at: p.updatedAt,
      test_cases: p.testCases,
      health: { key: 'no_data', label: 'No data', tone: 'neutral' },
    }));
  }, [data?.projects, activeProjects, allWorkspaces, scopeProject]);

  const healthCounts = useMemo(() => {
    const counts = { all: projects.length };
    projects.forEach((p) => {
      const key = p.health?.key || 'no_data';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects
      .filter((p) => healthFilter === 'all' || (p.health?.key || 'no_data') === healthFilter)
      .filter((p) => {
        if (!q) return true;
        const desc = descriptions.get(String(p.project_id)) || '';
        return String(p.project_name || '').toLowerCase().includes(q) || desc.toLowerCase().includes(q);
      })
      .sort(
        (a, b) =>
          (HEALTH_ORDER[a.health?.key] ?? 9) - (HEALTH_ORDER[b.health?.key] ?? 9) ||
          timeOf(b.updated_at) - timeOf(a.updated_at)
      );
  }, [projects, query, healthFilter, descriptions]);

  const lastUpdated = useMemo(
    () => projects.reduce((latest, p) => (timeOf(p.updated_at) > timeOf(latest?.updated_at) ? p : latest), null),
    [projects]
  );

  const passSpark = useMemo(() => {
    const values = (data?.trend?.buckets || []).map((b) => b.pass_rate).filter(isNum);
    return values.length > 1 ? { data: values } : undefined;
  }, [data?.trend?.buckets]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    const health = kpis.health || {};
    const healthBits = [
      health.critical ? `${health.critical} critical` : null,
      health.attention ? `${health.attention} need attention` : null,
      health.healthy ? `${health.healthy} healthy` : null,
    ].filter(Boolean);
    return [
      {
        label: 'Projects',
        value: fmtCount(kpis.projects),
        icon: <LuFolderKanban size={14} />,
        context:
          [allWorkspaces ? `${plural(kpis.workspaces || 0, 'workspace')}` : null, ...healthBits]
            .filter(Boolean)
            .join(' · ') || 'No runs in this period',
        tone: health.critical ? 'danger' : undefined,
        onClick: () => navigateTo('projects'),
      },
      {
        label: 'Team members',
        value: fmtCount(kpis.members),
        icon: <LuUsers size={14} />,
        context: 'Active across your projects',
      },
      {
        label: 'Test cases',
        value: fmtCount(kpis.test_cases),
        icon: <LuFlaskConical size={14} />,
        context: isNum(kpis.automation?.coverage)
          ? `${fmtPct(kpis.automation.coverage)} automated (${fmtCount(kpis.automation.automated)})`
          : 'No test cases yet',
      },
      {
        label: 'Pass rate',
        value: fmtPct(kpis.pass_rate?.current),
        icon: <LuPercent size={14} />,
        trend: comparisonText(kpis.pass_rate, { unit: 'pct' }),
        context: kpis.pass_rate?.evaluated
          ? `${fmtCount(kpis.pass_rate.evaluated)} evaluated vs ${rangeLabel}`
          : 'No pass/fail results in this period',
        sparkline: passSpark,
      },
      {
        label: 'Tests executed',
        value: fmtCount(kpis.tests_executed?.current),
        icon: <LuListChecks size={14} />,
        trend: comparisonText(kpis.tests_executed),
        context: `${fmtCount(kpis.tests_executed?.results)} results recorded`,
      },
      {
        label: 'Failing tests',
        value: fmtCount(kpis.failing_tests?.current),
        icon: <LuCircleX size={14} />,
        trend: comparisonText(kpis.failing_tests),
        trendInvert: true,
        tone: kpis.failing_tests?.current ? 'danger' : undefined,
        context: 'Latest result is a failure',
      },
      {
        label: 'Runs',
        value: fmtCount(kpis.runs?.current),
        icon: <LuPlay size={14} />,
        trend: comparisonText(kpis.runs),
        context: `Plan and suite runs vs ${rangeLabel}`,
      },
      {
        label: 'Avg run duration',
        value: fmtDuration(kpis.avg_run_duration?.current),
        icon: <LuTimer size={14} />,
        trend: comparisonText(kpis.avg_run_duration, { unit: 'seconds' }),
        trendInvert: true,
        context: 'Finished runs only',
      },
    ];
  }, [kpis, navigateTo, passSpark, rangeLabel, allWorkspaces]);

  const breakdown = useMemo(() => {
    const sums = { results: 0, passed: 0, failed: 0 };
    (data?.trend?.buckets || []).forEach((b) => {
      sums.results += b.results || 0;
      sums.passed += b.passed || 0;
      sums.failed += b.failed || 0;
    });
    return { ...sums, other: Math.max(sums.results - sums.passed - sums.failed, 0) };
  }, [data?.trend?.buckets]);
  const statusChartData = useMemo(
    () => [breakdown.results, breakdown.passed, breakdown.failed, breakdown.other],
    [breakdown]
  );

  const slowestPlans = data?.slowest_plans || [];

  const actOnInsight = (item) => {
    const action = item.action;
    if (!action) return;
    if (action.type === 'project') {
      const project = projects.find((p) => String(p.project_id) === String(action.project_id));
      if (project) openProject(project);
    } else if (action.type === 'schedules') {
      const target = projects[0];
      if (target) openInProject(target, 'schedules');
    }
  };

  const insights = data?.insights || [];
  const recentRuns = data?.recent_runs || [];
  const topFailures = data?.top_failures || [];
  const schedules = data?.schedules || { active: 0, upcoming: [] };

  return (
    <div className="rt2-overview rt-wh">
      <header className="rt-wh-hero">
        <div className="rt-wh-hero__text">
          <span className="rt-wh-hero__eyebrow">
            <LuLayoutGrid size={12} /> {scopeLabel}
            {selectedProjectName ? ` / ${selectedProjectName}` : ''}
          </span>
          <h1 className="rt-wh-hero__title">
            {greeting}, {displayName}
          </h1>
          <p className="rt-wh-hero__desc">
            {selectedProjectName
              ? `Quality report for ${selectedProjectName}.`
              : allWorkspaces
                ? 'Quality across every project you can access, in all workspaces.'
                : 'Quality across every project in this workspace.'}{' '}
            Open a project to dive into test cases, plans, and executions.
          </p>
          {data?.generated_at && (
            <span className="rt-wh-updated pqd-muted pqd-small">
              Updated {fmtRelative(data.generated_at)}
              <button
                type="button"
                className="rt-wh-refresh"
                onClick={() => overview.reload()}
                aria-label="Refresh workspace data"
                disabled={overview.refreshing}
              >
                <LuRefreshCw size={12} className={overview.refreshing ? 'rt-wh-spin' : ''} />
              </button>
            </span>
          )}
        </div>
        <div className="rt-wh-hero__actions">
          <button type="button" className="rt-btn rt-btn--primary" onClick={() => navigateTo('projects')}>
            <LuPlus size={14} /> New Project
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={() => navigateTo('all-workspaces')}>
            All Workspaces
          </button>
        </div>
      </header>

      <div className="rt-wh-filters" role="group" aria-label="Report filters">
        <label className="rt-wh-filter">
          <span className="rt-wh-filter__label">
            <LuLayoutGrid size={12} aria-hidden="true" /> Workspace
          </span>
          <select
            className="rt-wh-filter__select"
            value={scopeWorkspace}
            onChange={(e) => changeWorkspaceScope(e.target.value)}
          >
            <option value={ALL}>All workspaces</option>
            {workspaceOptions.map((w) => (
              <option key={w.id} value={String(w.id)}>
                {w.name || `Workspace ${w.id}`}
              </option>
            ))}
          </select>
        </label>

        <label className="rt-wh-filter">
          <span className="rt-wh-filter__label">
            <LuFolderKanban size={12} aria-hidden="true" /> Project
          </span>
          <select
            className="rt-wh-filter__select"
            value={scopeProject}
            onChange={(e) => {
              setScopeProject(e.target.value);
              setHealthFilter('all');
            }}
            disabled={projectOptionGroups.length === 0}
          >
            <option value="">All projects</option>
            {allWorkspaces
              ? projectOptionGroups.map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((p) => (
                      <option key={p.project_id} value={String(p.project_id)}>
                        {p.project_name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : projectOptionGroups.flatMap(([, items]) => items).map((p) => (
                  <option key={p.project_id} value={String(p.project_id)}>
                    {p.project_name}
                  </option>
                ))}
          </select>
        </label>

        <div className="rt-wh-filter">
          <span className="rt-wh-filter__label">
            <LuCalendarClock size={12} aria-hidden="true" /> Period
          </span>
          <div className="pqd-segmented" role="radiogroup" aria-label="Time range">
            {RANGES.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={range === opt.id}
                className={`pqd-segmented__item${range === opt.id ? ' is-active' : ''}`}
                onClick={() => setRange(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rt-wh-filters__end">
          {scopeChanged && (
            <button type="button" className="rt2-link" onClick={resetScope}>
              Reset to current workspace
            </button>
          )}
          {scopeProject && (
            <button
              type="button"
              className="rt-btn rt-btn--secondary rt-btn--sm"
              onClick={() => {
                const p = (data?.options?.projects || []).find((x) => String(x.project_id) === scopeProject);
                if (p) openProject(p);
              }}
            >
              Open project dashboard <LuArrowRight size={13} />
            </button>
          )}
        </div>
      </div>

      {lastUpdated && (
        <button type="button" className="rt-wh-continue" onClick={() => openProject(lastUpdated)}>
          <span className="rt-wh-continue__icon">
            <LuHistory size={16} />
          </span>
          <span className="rt-wh-continue__text">
            <span className="rt2-muted">Jump back in</span>
            <strong>{lastUpdated.project_name}</strong>
            {lastUpdated.updated_at && (
              <span className="rt2-muted">Updated {fmtRelative(lastUpdated.updated_at)}</span>
            )}
          </span>
          <LuArrowRight size={16} className="rt-wh-continue__arrow" />
        </button>
      )}

      {overview.error && !data ? (
        <SectionCard
          title="Workspace summary"
          error={overview.error}
          onRetry={overview.retry}
        />
      ) : (
        <section className="rt-metric-grid rt-wh-kpis" aria-label="Workspace summary">
          {sectionLoading
            ? Array.from({ length: 8 }, (_, i) => <MetricCard key={i} label="…" value="…" />)
            : kpiCards.map((item) => (
                <MetricCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  icon={item.icon}
                  trend={item.trend || undefined}
                  trendInvert={item.trendInvert}
                  context={item.context}
                  tone={item.tone}
                  sparkline={item.sparkline}
                  onClick={item.onClick}
                />
              ))}
        </section>
      )}

      {insights.length > 0 && (
        <ul className="pqd-insights rt-wh-insights" aria-label="Workspace insights">
          {insights.map((item) => {
            const sev = SEVERITY[item.severity] || SEVERITY.info;
            const Icon = sev.icon;
            return (
              <li key={item.id} className={`pqd-insight pqd-insight--${sev.tone}`}>
                <Icon size={16} aria-hidden="true" className="pqd-insight__icon" />
                <div className="pqd-insight__body">
                  <strong>{item.title}</strong>
                  {item.detail && <p>{item.detail}</p>}
                </div>
                {item.action && (
                  <button
                    type="button"
                    className="rt-btn rt-btn--tertiary rt-btn--sm"
                    onClick={() => actOnInsight(item)}
                  >
                    {item.action.type === 'schedules' ? 'Schedule a plan' : 'Open project'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <QualityTrends
        summary={data ? { trends: data.trend } : null}
        loading={sectionLoading}
        error={overview.error}
        onRetry={overview.retry}
      />

      <div className="rt2-panel rt-wh-projects">
        <div className="rt2-panel__header rt-wh-projects__header">
          <h2 className="rt2-panel__title">
            Project health <span className="rt-wh-count">{projects.length}</span>
          </h2>
          <div className="rt-wh-toolbar">
            <label className="rt-wh-search">
              <LuSearch size={13} aria-hidden="true" />
              <input
                type="search"
                placeholder="Filter projects"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Filter projects"
              />
            </label>
            <button type="button" className="rt2-link" onClick={() => navigateTo('projects')}>
              View all
            </button>
          </div>
        </div>

        {data?.projects && projects.length > 1 && (
          <div className="rt-wh-chips" role="tablist" aria-label="Filter by health">
            {HEALTH_FILTERS.filter((f) => f.id === 'all' || healthCounts[f.id]).map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={healthFilter === f.id}
                className={`rt-wh-chip${healthFilter === f.id ? ' rt-wh-chip--active' : ''}`}
                onClick={() => setHealthFilter(f.id)}
              >
                {f.label}
                <span className="rt-wh-chip__count">{healthCounts[f.id] || 0}</span>
              </button>
            ))}
          </div>
        )}

        <div className="rt2-panel__body rt2-table-wrap" style={{ padding: 0 }}>
          <table className="rt2-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Health</th>
                <th>Pass rate</th>
                <th>Executed</th>
                <th>Failing</th>
                <th>Automation</th>
                <th>Last run</th>
                <th>Members</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {(sectionLoading && loadingProjects) || (sectionLoading && projects.length === 0) ? (
                <tr>
                  <td colSpan={9} className="rt2-empty">Loading projects…</td>
                </tr>
              ) : null}
              {!sectionLoading && projects.length === 0 && (
                <tr>
                  <td colSpan={9} className="rt2-empty">No active projects. Create one to get started.</td>
                </tr>
              )}
              {projects.length > 0 && filteredProjects.length === 0 && (
                <tr>
                  <td colSpan={9} className="rt2-empty">No projects match your filter.</td>
                </tr>
              )}
              {filteredProjects.map((project) => {
                const desc = descriptions.get(String(project.project_id));
                const lastRun = project.last_run;
                const outcome = lastRun ? RUN_OUTCOME[lastRun.outcome] || RUN_OUTCOME.unknown : null;
                return (
                  <tr key={project.project_id} className="rt-wh-row" onClick={() => openProject(project)}>
                    <td>
                      <button
                        type="button"
                        className="rt2-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProject(project);
                        }}
                      >
                        {project.project_name}
                      </button>
                      <div className="rt-wh-project-meta">
                        {project.project_type && <ProjectTypeBadge type={project.project_type} />}
                        {allWorkspaces && project.workspace_name && (
                          <span className="rt-wh-ws-tag">
                            <LuLayoutGrid size={10} aria-hidden="true" /> {project.workspace_name}
                          </span>
                        )}
                        {desc && desc !== 'No description' && <span className="rt2-muted rt-wh-desc">{desc}</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`rt-badge rt-badge--${project.health?.tone || 'neutral'}`}>
                        {project.health?.label || 'No data'}
                      </span>
                    </td>
                    <td className="rt-wh-num">
                      {fmtPct(project.pass_rate?.current)}
                      <DeltaText kpi={project.pass_rate} unit="pct" />
                    </td>
                    <td className="rt-wh-num">
                      {isNum(project.tests_executed) ? (
                        <>
                          {fmtCount(project.tests_executed)}
                          <span className="pqd-muted pqd-small"> / {fmtCount(project.test_cases)}</span>
                        </>
                      ) : (
                        fmtCount(project.test_cases)
                      )}
                    </td>
                    <td className={`rt-wh-num${project.failing_tests ? ' is-danger' : ''}`}>
                      {fmtCount(project.failing_tests)}
                    </td>
                    <td>
                      <AutomationBar
                        automated={project.automated}
                        total={project.test_cases}
                        coverage={project.automation_coverage}
                      />
                    </td>
                    <td>
                      {lastRun ? (
                        <div className="rt-wh-lastrun">
                          <span className={`rt-status-dot rt-status-dot--${outcome.tone}`} title={outcome.label} />
                          <span className="rt-wh-lastrun__text">
                            <span className="rt-wh-desc">{lastRun.name}</span>
                            <span className="pqd-muted pqd-small">{fmtRelative(lastRun.started_at)}</span>
                          </span>
                        </div>
                      ) : (
                        <span className="pqd-muted">Never run</span>
                      )}
                    </td>
                    <td className="rt-wh-num">{fmtCount(project.members)}</td>
                    <td className="rt-wh-open">
                      <LuArrowRight size={14} aria-hidden="true" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pqd-grid pqd-grid--2">
        <SectionCard
          id="rt-wh-runs"
          title="Recent runs"
          subtitle="Latest plan and suite runs across all projects."
          loading={sectionLoading}
          error={overview.error}
          onRetry={overview.retry}
        >
          {recentRuns.length === 0 ? (
            <SectionEmpty title="No runs yet">Run a test plan or suite to see it here.</SectionEmpty>
          ) : (
            <ul className="rt-wh-list">
              {recentRuns.map((run) => {
                const outcome = RUN_OUTCOME[run.outcome] || RUN_OUTCOME.unknown;
                const t = run.totals || {};
                return (
                  <li key={`${run.type}-${run.id}`}>
                    <button type="button" className="rt-wh-list__item" onClick={() => openInProject(run.project, runPath(run))}>
                      <span className={`rt-status-dot rt-status-dot--${outcome.tone}`} title={outcome.label} />
                      <span className="rt-wh-list__main">
                        <span className="pqd-strong rt-wh-desc">{run.name}</span>
                        <span className="pqd-muted pqd-small">
                          {run.project?.project_name} · {run.type === 'plan' ? 'Test plan' : 'Suite'}
                          {run.executed_by ? ` · ${run.executed_by}` : ''} · {fmtRelative(run.started_at)}
                        </span>
                      </span>
                      <span className="rt-wh-list__side">
                        {run.state === 'running' ? (
                          <span className="rt-badge rt-badge--info">Running</span>
                        ) : (
                          <>
                            <span className="pqd-num">
                              <span className="is-success">{t.passed ?? 0}</span>
                              {' / '}
                              <span className="is-danger">{t.failed ?? 0}</span>
                              <span className="pqd-muted"> of {t.total ?? 0}</span>
                            </span>
                            <span className="pqd-muted pqd-small">{fmtDuration(run.duration)}</span>
                          </>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          id="rt-wh-failures"
          title="Top failing tests"
          subtitle={`Most failures in the last ${range === '7d' ? '7' : '30'} days.`}
          loading={sectionLoading}
          error={overview.error}
          onRetry={overview.retry}
        >
          {topFailures.length === 0 ? (
            <SectionEmpty title="No failures">No test failed in this period.</SectionEmpty>
          ) : (
            <ul className="rt-wh-list">
              {topFailures.map((row) => (
                <li key={row.test_case_id}>
                  <button
                    type="button"
                    className="rt-wh-list__item"
                    onClick={() => openInProject(row.project, `test-cases/${row.test_case_id}`)}
                  >
                    <LuCircleX size={15} className="is-danger" aria-hidden="true" />
                    <span className="rt-wh-list__main">
                      <span className="pqd-strong rt-wh-desc">{row.name}</span>
                      <span className="pqd-muted pqd-small">
                        {row.project?.project_name}
                        {row.priority ? ` · ${row.priority}` : ''} · last failed {fmtRelative(row.last_failed_at)}
                      </span>
                    </span>
                    <span className="rt-wh-list__side">
                      <span className="pqd-num is-danger">{plural(row.failures, 'failure')}</span>
                      <span className="pqd-muted pqd-small">of {plural(row.results, 'run')}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="rt-wh-charts rt-wh-charts--three">
        <div className="rt2-panel">
          <div className="rt2-panel__header">
            <h2 className="rt2-panel__title">Test results</h2>
            <span className="pqd-muted pqd-small">Last {range === '7d' ? '7' : '30'} days</span>
          </div>
          <div className="rt2-panel__body">
            {sectionLoading ? (
              <div className="rt2-empty">Loading…</div>
            ) : breakdown.results === 0 ? (
              <div className="rt2-empty">No test results in this period. Run a test plan to see the breakdown.</div>
            ) : (
              <div className="rt-wh-donut">
                <div className="rt-wh-donut__canvas">
                  <TestStatusChart
                    data={statusChartData}
                    totalTestCases={breakdown.results}
                    labels={['Passed', 'Failed', 'Skipped / other']}
                  />
                </div>
                <ul className="rt-wh-legend">
                  {[
                    { label: 'Passed', value: breakdown.passed, tone: 'success' },
                    { label: 'Failed', value: breakdown.failed, tone: 'danger' },
                    { label: 'Skipped / other', value: breakdown.other, tone: 'warning' },
                  ].map((row) => (
                    <li key={row.label}>
                      <span className={`rt-status-dot rt-status-dot--${row.tone}`} />
                      <span>{row.label}</span>
                      <strong>{row.value.toLocaleString()}</strong>
                      <span className="rt2-muted">{Math.round((row.value / breakdown.results) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="rt2-panel">
          <div className="rt2-panel__header">
            <h2 className="rt2-panel__title">
              Upcoming schedules <span className="rt-wh-count">{schedules.active}</span>
            </h2>
          </div>
          <div className="rt2-panel__body">
            {sectionLoading ? (
              <div className="rt2-empty">Loading…</div>
            ) : schedules.upcoming.length === 0 ? (
              <div className="rt2-empty">No scheduled runs. Schedule a test plan to run it automatically.</div>
            ) : (
              <ul className="rt-wh-list">
                {schedules.upcoming.map((s) => (
                  <li key={s.id}>
                    <button type="button" className="rt-wh-list__item" onClick={() => openInProject(s.project, 'schedules')}>
                      <LuCalendarClock size={15} className="pqd-muted" aria-hidden="true" />
                      <span className="rt-wh-list__main">
                        <span className="pqd-strong rt-wh-desc">{s.name || s.test_plan?.name || `Schedule #${s.id}`}</span>
                        <span className="pqd-muted pqd-small">
                          {s.project?.project_name}
                          {s.test_plan?.name ? ` · ${s.test_plan.name}` : ''}
                        </span>
                      </span>
                      <span className="rt-wh-list__side">
                        <span className="pqd-small">{s.next_run_at ? fmtDateTime(s.next_run_at) : '—'}</span>
                        <span className="pqd-muted pqd-small">{SCHEDULE_LABEL[s.schedule_type] || s.schedule_type}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="rt2-panel">
          <div className="rt2-panel__header">
            <h2 className="rt2-panel__title">Slowest plans &amp; suites</h2>
            <span className="pqd-muted pqd-small">Avg. duration</span>
          </div>
          <div className="rt2-panel__body">
            {sectionLoading ? (
              <div className="rt2-empty">Loading…</div>
            ) : slowestPlans.length === 0 ? (
              <div className="rt2-empty">No finished plan or suite runs in this period.</div>
            ) : (
              <HorizontalBarChart
                labels={slowestPlans.map((p) => p.name)}
                values={slowestPlans.map((p) => p.avg_duration)}
                color="warning"
                formatValue={(v) => {
                  const plan = slowestPlans.find((p) => p.avg_duration === v);
                  const extra = plan
                    ? ` · ${plan.type === 'plan' ? 'Test plan' : 'Suite'} · ${plural(plan.runs, 'run')} · ${plan.project?.project_name || ''}`
                    : '';
                  return `${fmtDuration(Number(v))} avg${extra}`;
                }}
                onBarClick={(i) => {
                  const item = slowestPlans[i];
                  if (item) openInProject(item.project, item.type === 'plan' ? 'test-plans' : 'test-suites');
                }}
                height={Math.max(160, slowestPlans.length * 36)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceHome;
